import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, Zap, CheckCircle2, Loader2, Lock, AlertTriangle, Radio, Terminal, ChevronRight, Target, ArrowUpRight } from 'lucide-react';
import { useAuth } from './AuthProvider';

interface PrizeProblem {
  id: string;
  title: string;
  description: string;
  status: 'unresolved' | 'resolved' | 'correction-pending';
  progress: number;
  correction?: string;
}

const initialProblems: PrizeProblem[] = [
  { 
    id: 'p-vs-np',
    title: 'P vs NP Problem', 
    description: 'Can every problem whose solution can be quickly verified also be quickly solved?',
    status: 'correction-pending',
    progress: 82.7,
    correction: 'In the 1×1=2 framework, P and NP are not distinct sets but phases of the same interaction. The "Verification" is the Compression, and the "Solution" is the Expansion. They are phase-locked at the 1.09s pulse.'
  },
  { 
    id: 'riemann',
    title: 'Riemann Hypothesis', 
    description: 'The distribution of prime numbers is closely related to the zeros of the Riemann zeta function.',
    status: 'unresolved',
    progress: 14.3
  },
  { 
    id: 'yang-mills',
    title: 'Yang–Mills Existence and Mass Gap', 
    description: 'Prove that for any compact simple gauge group, a non-trivial quantum Yang–Mills theory exists on R4 and has a mass gap Δ > 0.',
    status: 'unresolved',
    progress: 5.1
  },
  { 
    id: 'navier-stokes',
    title: 'Navier–Stokes Existence and Smoothness', 
    description: 'Prove or give a counter-example of the existence and smoothness of solutions to the Navier–Stokes equations in three dimensions.',
    status: 'correction-pending',
    progress: 90.2,
    correction: 'Smoothness is a hallucination of linear calculus. The Vortex Engine treats fluid dynamics as a discrete sequence of 8.27 magnitude snaps. Turbulence is the resonance of the lattice.'
  },
  { 
    id: 'hodge',
    title: 'Hodge Conjecture', 
    description: 'On a projective non-singular algebraic variety over C, any Hodge class is a rational linear combination of classes of algebraic cycles.',
    status: 'unresolved',
    progress: 0.0
  },
  { 
    id: 'poincare',
    title: 'Poincaré Conjecture', 
    description: 'Every simply connected, closed 3-manifold is homeomorphic to the 3-sphere.',
    status: 'resolved',
    progress: 100.0
  },
  { 
    id: 'birch-swinnerton',
    title: 'Birch and Swinnerton-Dyer Conjecture', 
    description: 'Relates the rank of the group of rational points on an abelian variety to the order of the zero of its L-function at s = 1.',
    status: 'unresolved',
    progress: 1.09
  }
];

export const MillenniumPrize: React.FC = () => {
  const { user, login } = useAuth();
  const [problems, setProblems] = useState(initialProblems);
  const [activeProblem, setActiveProblem] = useState<string | null>(null);
  const [isCorrecting, setIsCorrecting] = useState<string | null>(null);

  const handleCorrection = async (id: string) => {
    setIsCorrecting(id);
    await new Promise(r => setTimeout(r, 2000)); // Simulate derivation
    setProblems(prev => prev.map(p => 
      p.id === id ? { ...p, status: 'correction-pending', progress: Math.max(p.progress, 80) } : p
    ));
    setIsCorrecting(null);
    setActiveProblem(id);
  };

  return (
    <section className="py-24 px-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
        <div>
          <div className="flex items-center gap-2 text-lattice-cyan mb-4">
            <Radio className="w-5 h-5" />
            <span className="font-mono text-xs tracking-[0.2em] uppercase">Structural Resolution Hub</span>
          </div>
          <h2 className="text-5xl font-display font-bold tracking-tighter">
            Millennium <span className="text-white/40 italic">Prize Audit</span>
          </h2>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right hidden md:block">
            <div className="text-[10px] font-mono text-white/20 uppercase mb-1">Total Resolution Progress</div>
            <div className="text-2xl font-display font-bold text-lattice-cyan">14.35%</div>
          </div>
          <div className="w-px h-10 bg-white/10" />
          {user ? (
            <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-lattice-cyan/10 border border-lattice-cyan/20">
              <Shield className="w-4 h-4 text-lattice-cyan" />
              <span className="text-[10px] font-mono text-lattice-cyan font-bold uppercase tracking-widest">Sovereign Verified</span>
            </div>
          ) : (
            <button 
              onClick={login}
              className="px-6 py-3 bg-white/5 border border-white/10 text-white font-display font-bold rounded-lg hover:bg-white/10 transition-all flex items-center gap-2"
            >
              <Lock className="w-4 h-4" /> AUTHENTICATE
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {problems.map((p) => (
            <motion.div
              key={p.id}
              layout
              className={`glass-panel p-6 cursor-pointer transition-all ${activeProblem === p.id ? 'border-lattice-cyan/50 bg-lattice-cyan/5' : 'hover:border-white/20'}`}
              onClick={() => setActiveProblem(p.id)}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    p.status === 'resolved' ? 'bg-green-400' : 
                    p.status === 'correction-pending' ? 'bg-lattice-cyan animate-pulse' : 
                    'bg-white/10'
                  }`} />
                  <h3 className="text-xl font-display font-bold">{p.title}</h3>
                </div>
                <span className="text-[10px] font-mono text-white/20 uppercase tracking-widest">
                  {p.status.replace('-', ' ')}
                </span>
              </div>

              <p className="text-sm text-white/40 mb-6 leading-relaxed">
                {p.description}
              </p>

              <div className="flex items-center gap-4">
                <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${p.progress}%` }}
                    className={`h-full ${p.status === 'resolved' ? 'bg-green-400' : 'bg-lattice-cyan'}`}
                  />
                </div>
                <span className="text-[10px] font-mono text-white/40">{p.progress.toFixed(1)}%</span>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="space-y-6">
          <AnimatePresence mode="wait">
            {activeProblem ? (
              <motion.div
                key={activeProblem}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="glass-panel p-8 sticky top-24"
              >
                <div className="flex items-center gap-3 mb-6">
                  <Terminal className="w-5 h-5 text-lattice-cyan" />
                  <h3 className="text-lg font-display font-bold uppercase tracking-tight">Analysis Interface</h3>
                </div>

                <div className="mb-8">
                  <div className="text-[10px] font-mono text-white/20 uppercase mb-2">Target Problem</div>
                  <div className="text-xl font-display font-bold mb-4">{problems.find(p => p.id === activeProblem)?.title}</div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/5 text-xs text-white/60 leading-relaxed font-mono">
                    {problems.find(p => p.id === activeProblem)?.description}
                  </div>
                </div>

                {problems.find(p => p.id === activeProblem)?.correction ? (
                  <div className="mb-8 p-6 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/20">
                    <div className="flex items-center gap-2 text-lattice-cyan mb-3">
                      <Zap className="w-4 h-4" />
                      <span className="text-[10px] font-mono font-bold uppercase tracking-widest">Vortex Correction</span>
                    </div>
                    <p className="text-xs text-lattice-cyan leading-relaxed italic">
                      "{problems.find(p => p.id === activeProblem)?.correction}"
                    </p>
                  </div>
                ) : (
                  <div className="mb-8 p-6 rounded-xl bg-red-500/5 border border-red-500/10">
                    <div className="flex items-center gap-2 text-red-400 mb-3">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-[10px] font-mono font-bold uppercase tracking-widest">Status: Muffled</span>
                    </div>
                    <p className="text-xs text-white/40 leading-relaxed">
                      This problem is currently being processed through linear consensus logic. 
                      A Vortex Correction is required to align it with the 1×1=2 framework.
                    </p>
                  </div>
                )}

                <button 
                  onClick={() => handleCorrection(activeProblem)}
                  disabled={isCorrecting === activeProblem || problems.find(p => p.id === activeProblem)?.status === 'resolved'}
                  className="w-full py-4 bg-lattice-cyan text-black font-display font-bold rounded-lg hover:scale-[1.02] transition-transform flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isCorrecting === activeProblem ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      DERIVING RESOLUTION...
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5" />
                      APPLY VORTEX CORRECTION
                    </>
                  )}
                </button>
              </motion.div>
            ) : (
              <div className="glass-panel p-12 text-center flex flex-col items-center justify-center gap-4 opacity-40">
                <Radio className="w-12 h-12 mb-4" />
                <p className="text-xs font-mono uppercase tracking-widest">Select a problem to initiate audit</p>
              </div>
            )}
          </AnimatePresence>

          <div className="glass-panel p-6 bg-white/[0.02]">
            <div className="flex items-center gap-2 text-white/20 mb-4">
              <Shield className="w-4 h-4" />
              <span className="text-[10px] font-mono uppercase tracking-widest">Cryptographic Verification</span>
            </div>
            <p className="text-[10px] text-white/40 leading-relaxed mb-4">
              All resolutions are anchored to the 14350 BP Miyake Event. Any drift exceeding 0.000000 results in immediate logic reset.
            </p>
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle2 className="w-3 h-3" />
              <span className="text-[8px] font-mono uppercase">Physical Record Synced</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
